<template>
  <div class="fee-config-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('feeConfigManage.feeItems')" :subtitle="$t('feeConfigManage.page.subtitle')">
      <template #extra>
        <el-button v-if="hasPrivilege('502022022571930560')" type="primary" icon="el-icon-plus"
          @click="_openAddFeeConfigModal">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <div class="fee-config-layout">
      <!-- 左侧费用类型筛选 -->
      <div class="ds-panel ds-panel--tree">
        <ul class="state-list">
          <li v-for="(item, index) in feeTypeCds" :key="index" @click="swatchFeeTypeCd(item)"
            :class="{ 'active-state': conditions.feeTypeCd == item.statusCd }">
            {{ item.name }}
          </li>
        </ul>
      </div>

      <div class="fee-config-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.configId" clearable class="w-full"
                :placeholder="$t('feeConfigManage.enterFeeItemId')" @keyup.enter.native="_queryFeeConfigMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.feeName" clearable class="w-full"
                :placeholder="$t('feeConfigManage.enterFeeItem')" @keyup.enter.native="_queryFeeConfigMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.feeFlag" clearable class="w-full"
                :placeholder="$t('feeConfigManage.selectFeeFlag')">
                <el-option v-for="item in feeFlags" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.paymentCd" clearable class="w-full"
                :placeholder="$t('feeConfigManage.selectPaymentType')">
                <el-option v-for="item in paymentCds" :key="item.statusCd" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.deductFrom" clearable class="w-full"
                :placeholder="$t('feeConfigManage.selectAccountDeduction')">
                <el-option :label="$t('common.yes')" value="Y" />
                <el-option :label="$t('common.no')" value="N" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.isDefault" clearable class="w-full"
                :placeholder="$t('feeConfigManage.selectDefaultFee')">
                <el-option v-for="item in isDefaults" :key="item.statusCd" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryFeeConfigMethod">
              {{ $t('common.query') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetFeeConfigMethod">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="_listFeeConfigs">
            <template #left>
              <span class="ds-panel__title">{{ $t('feeConfigManage.feeItems') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="feeConfigs" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('feeConfigManage.page.empty')" @retry="_listFeeConfigs">
            <el-table-column prop="configId" :label="$t('feeConfigManage.number')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.configId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="feeTypeCdName" :label="$t('feeConfigManage.feeType')" align="center" width="120">
              <template slot-scope="scope">
                {{ scope.row.feeTypeCdName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="feeName" :label="$t('feeConfigManage.feeItem')" align="left" min-width="150"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.feeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="feeFlagName" :label="$t('feeConfigManage.feeFlag')" align="center" width="130">
              <template slot-scope="scope">
                {{ scope.row.feeFlagName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('feeConfigManage.paymentType')" align="center" width="120">
              <template slot-scope="scope">
                {{ scope.row.paymentCd == '1200' ? $t('feeConfigManage.prepaid') : $t('feeConfigManage.postpaid') }}
              </template>
            </el-table-column>
            <el-table-column prop="paymentCycle" :label="$t('feeConfigManage.paymentCycle')" align="center" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.paymentCycle || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="computingFormulaName" :label="$t('feeConfigManage.formula')" align="center" min-width="130"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.computingFormulaName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('feeConfigManage.unitPrice')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.computingFormula == '2002' ? '-' : formatMoney(scope.row.squarePrice) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="additionalAmount" :label="$t('feeConfigManage.additionalFee')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.additionalAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('feeConfigManage.accountDeduction')" align="center" width="120">
              <template slot-scope="scope">
                {{ scope.row.deductFrom == 'Y' ? $t('common.yes') : $t('common.no') }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.status')" align="center" width="100">
              <template slot-scope="scope">
                <ds-status-tag :type="scope.row.state == 'Y' ? 'success' : 'muted'"
                  :label="scope.row.state == 'Y' ? $t('common.enabled') : $t('common.disabled')" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="280">
              <template slot-scope="scope">
                <el-button v-if="hasPrivilege('502022022549630561')" type="text" size="small"
                  @click="_openEditFeeConfigModel(scope.row)">
                  {{ $t('common.edit') }}
                </el-button>
                <el-button type="text" size="small" @click="_settingConfigDiscount(scope.row)">
                  {{ $t('feeConfigManage.discount') }}
                </el-button>
                <el-button v-if="scope.row.isDefault == 'F' && hasPrivilege('502022022586400562')" type="text" size="small"
                  class="ds-btn-danger" @click="_openDeleteFeeConfigModel(scope.row)">
                  {{ $t('common.delete') }}
                </el-button>
                <el-button type="text" size="small" @click="_openFeeConfigDetail(scope.row)">
                  {{ $t('common.detail') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="total" :page="curPage" :size="pageSize" :page-sizes="[10, 20, 30, 50]"
            layout="total, prev, pager, next" @current-change="handlePageChange">
            <template #summary>
              <div class="ds-tip-text">
                <p>{{ $t('feeConfigManage.feeFlagTip') }}</p>
                <p>{{ $t('feeConfigManage.paymentTypeTip') }}</p>
                <p>{{ $t('feeConfigManage.accountDeductionTip') }}</p>
              </div>
            </template>
          </ds-pagination-bar>
        </div>
      </div>
    </div>

    <add-fee-config ref="addFeeConfig" @success="handleSuccess" />
    <edit-fee-config ref="editFeeConfig" @success="handleSuccess" />
    <delete-fee-config ref="deleteFeeConfig" @success="handleSuccess" />
  </div>
</template>

<script>
import { listFeeConfigs } from '@/api/fee/feeConfigManageApi'
import AddFeeConfig from '@/components/fee/addFeeConfig'
import EditFeeConfig from '@/components/fee/editFeeConfig'
import DeleteFeeConfig from '@/components/fee/deleteFeeConfig'
import { getDict } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'FeeConfigManageList',
  components: {
    AddFeeConfig,
    EditFeeConfig,
    DeleteFeeConfig,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      feeTypeCds: [],
      feeFlags: [],
      paymentCds: [],
      isDefaults: [],
      feeConfigs: [],
      total: 0,
      curPage: 1,
      pageSize: 10,
      moreCondition: false,
      conditions: {
        configId: '',
        feeFlag: '',
        feeName: '',
        feeTypeCd: '',
        isDefault: 'F',
        paymentCd: '',
        deductFrom: '',

      }
    }
  },
  created() {
    this._listFeeConfigs()
    this.loadDicts()
  },
  methods: {
    async loadDicts() {

      // 仅在费用项配置页面隐藏以下六类费用，避免误操作与报表干扰。
      const _HIDDEN_FEE_TYPE_CDS = [
        '888800010015', // 水费
        '888800010016', // 电费
        '888800010017', // 公摊费
        '888800010011', // 取暖费
        '888800010009', // 煤气费
        '888800010000'  // 系统费用
      ]
      const _rawFeeTypes = await getDict('pay_fee_config', 'fee_type_cd')
      const _feeTypes = _rawFeeTypes.filter(item => !_HIDDEN_FEE_TYPE_CDS.includes(item.statusCd))
      let _feeTypeCds = {
        name: this.$t('common.all'),
        statusCd: ''
      }
      this.feeTypeCds = [_feeTypeCds, ..._feeTypes]

      const feeFlags = await getDict('pay_fee_config', 'fee_flag')
      this.feeFlags = [{
        name: this.$t('common.all'),
        statusCd: ''
      }, ...feeFlags]

      const paymentCds = await getDict('pay_fee_config', 'payment_cd')
      this.paymentCds = [{
        name: this.$t('common.all'),
        statusCd: ''
      }, ...paymentCds]

      const isDefaults = await getDict('pay_fee_config', 'is_default')
      this.isDefaults = [{
        name: this.$t('common.all'),
        statusCd: ''
      }, ...isDefaults]

    },

    async _listFeeConfigs() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.conditions,
          page: this.curPage,
          row: this.pageSize,
          communityId: this.getCommunityId()
        }

        const { feeConfigs, total } = await listFeeConfigs(params)
        this.feeConfigs = Array.isArray(feeConfigs) ? feeConfigs : []
        this.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('feeConfigManage.page.loadFailed')
        this.$message.error(this.$t('feeConfigManage.page.loadFailed'))
      } finally {
        this.loading = false
      }
    },

    _queryFeeConfigMethod() {
      this.curPage = 1
      this._listFeeConfigs()
    },

    _resetFeeConfigMethod() {
      this.conditions = {
        configId: '',
        feeFlag: '',
        feeName: '',
        feeTypeCd: '',
        isDefault: 'F',
        paymentCd: '',
        deductFrom: ''
      }
      this.curPage = 1
      this._listFeeConfigs()
    },

    _moreCondition() {
      this.moreCondition = !this.moreCondition
    },

    swatchFeeTypeCd(item) {
      this.conditions.feeTypeCd = item.statusCd
      this.curPage = 1
      this._listFeeConfigs()
    },

    _openAddFeeConfigModal() {
      this.$refs.addFeeConfig.open()
    },

    _openEditFeeConfigModel(feeConfig) {
      this.$refs.editFeeConfig.open(feeConfig)
    },

    _openDeleteFeeConfigModel(feeConfig) {
      this.$refs.deleteFeeConfig.open(feeConfig)
    },

    _settingConfigDiscount(feeConfig) {
      this.$router.push({
        path: '/views/fee/payFeeConfigDiscountManage',
        query: {
          configId: feeConfig.configId,
          feeName: feeConfig.feeName
        }
      })
    },

    _openFeeConfigDetail(feeConfig) {
      window.open(`/#/views/fee/feeConfigDetail?configId=${feeConfig.configId}`)
    },

    handlePageChange(page) {
      this.curPage = page
      this._listFeeConfigs()
    },

    handleSuccess() {
      this._listFeeConfigs()
    },

    showMarkdown() {
      // 显示文档的实现
    },

    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-config-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧类型筛选 + 右侧主内容 */
.fee-config-layout {
  display: flex;
  gap: var(--ds-spacing-md);
  align-items: flex-start;
}

.fee-config-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 左侧类型筛选面板 */
.ds-panel--tree {
  width: 180px;
  flex-shrink: 0;
}

.state-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    padding: 10px;
    text-align: center;
    cursor: pointer;
    border-radius: var(--ds-radius-sm);
    margin-bottom: 5px;
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    transition: background-color var(--ds-transition-fast), color var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.active-state {
      background-color: var(--ds-primary);
      color: #fff;
    }
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;

  p {
    margin: 0;
  }
}
</style>
